import React from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import styles from './TeamAssignment.module.css';

const TEAMS = [
  { id: 'blue', name: '블루', color: '#2563eb', value: '블루' },
  { id: 'red', name: '레드', color: '#dc2626', value: '레드' },
  { id: 'yellow', name: '옐로우', color: '#ca8a04', value: '옐로우' },
  { id: 'unassigned', name: '미배정', color: '#6b7280', value: null },
];

const PlayerCard = ({ player, index, teamId, onMove, onRemove }) => {
  const ref = React.useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: 'player',
    item: { id: player.id, index, teamId, currentTeam: player.team_name },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'player',
    hover: (item, monitor) => {
      if (!ref.current) return;
      
      // 같은 팀 내에서만 순서 변경 가능
      if (item.teamId !== teamId) return;
      if (item.index === index) return;

      // 마우스 포인터의 위치 확인
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // 드래그하는 아이템이 호버 아이템의 중간을 넘어갈 때만 이동
      if (item.index < index && hoverClientY < hoverMiddleY) return;
      if (item.index > index && hoverClientY > hoverMiddleY) return;

      onMove(teamId, item.index, index);
      item.index = index;
    },
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`${styles.playerCard} ${isDragging ? styles.dragging : ''}`}
    >
      <div className={styles.playerItem}>
        <div className={styles.playerInfo}>
            <span className={styles.playerNumber}>{index + 1}</span>
            <span className={styles.playerProfile}>
                <span className={styles.playerName}>{player.lineup_user.name}</span>
                <div className={styles.playerDetail}>
                    <span className={styles.playerLevel}>Lv.{player.lineup_user.level}</span>
                    <span className={styles.playerPhone}>{player.lineup_user.sex}</span>
                    <span className={styles.playerPhone}>{player.lineup_user.phone}</span>
                </div>
            </span>
        </div>
        <div className={styles.playerActions}>
          <button
            type="button"
            className={styles.removeButton}
            onClick={() => onRemove(player.id)}
            title="참가자 제거"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

const TeamColumn = ({ team, players, onTeamChange, onMove, onRemove }) => {
  const [{ isOver }, drop] = useDrop({
    accept: 'player',
    drop: (item) => {
      if (item.currentTeam !== team.value) {
        onTeamChange(item.id, team.id);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  // 팀별 플레이어 필터링 및 정렬
  const teamPlayers = team.id === 'unassigned'
    ? players.filter(p => !p.team_name)
    : players.filter(p => p.team_name === team.value);

  const sortedPlayers = [...teamPlayers].sort((a, b) => 
    (a.team_number || 0) - (b.team_number || 0)
  );
  
  // 팀 평균 레벨 계산
  const calculateAverageLevel = () => {
    // 레벨이 존재하고 0보다 큰 플레이어만 필터링
    const validPlayers = sortedPlayers.filter(player => 
      player.lineup_user?.level && player.lineup_user.level > 0
    );
    
    if (validPlayers.length === 0) return 0;
    
    const totalLevel = validPlayers.reduce((sum, player) => {
      return sum + player.lineup_user.level;
    }, 0);
    
    // 소수점 2자리까지 표시
    return (Math.round((totalLevel / validPlayers.length) * 100) / 100).toFixed(2);
  };
  
  const averageLevel = calculateAverageLevel();

  return (
    <div 
      className={styles.teamColumn}
      style={{ '--team-color': team.color }}
    >
        <div className={styles.teamHeader}>
            <h3 className={styles.teamTitle}>
                {team.name}
            </h3>
                {sortedPlayers.length > 0 && (
                <span className={styles.teamAvgLevel}> Lv.{averageLevel}</span>
                )}
        </div>
      <div
        ref={drop}
        className={`${styles.dropZone} ${isOver ? styles.draggingOver : ''}`}
      >
        {sortedPlayers.map((player, index) => (
          <PlayerCard
            key={player.id}
            player={player}
            index={index}
            teamId={team.id}
            onMove={onMove}
            onRemove={onRemove}
          />
        ))}
      </div>
    </div>
  );
};

function TeamAssignment({ players, onTeamChange, onMove: onMoveCallback, onRemove }) {
  // 팀 내에서 선수 순서 변경 및 등번호 업데이트
  const handleMove = (teamId, fromIndex, toIndex) => {
    // 현재 팀의 선수들 가져오기
    const teamPlayers = teamId === 'unassigned'
      ? players.filter(p => !p.team_name)
      : players.filter(p => p.team_name === TEAMS.find(t => t.id === teamId)?.value);
    
    // 정렬된 선수 목록
    const sortedPlayers = [...teamPlayers].sort((a, b) => 
      (a.team_number || 0) - (b.team_number || 0)
    );
    
    // 이동할 선수
    const movingPlayer = sortedPlayers[fromIndex];
    
    // 새로운 순서로 선수 배열 재구성
    const newOrder = [...sortedPlayers];
    newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, movingPlayer);
    
    // 등번호 재할당
    const updates = newOrder.map((player, index) => ({
      id: player.id,
      team_number: index + 1
    }));
    
    // 부모 컴포넌트의 onMove 함수 호출 (상태 업데이트 및 DB 업데이트)
    onMoveCallback(teamId, updates);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={styles.container}>
        {TEAMS.map(team => (
          <TeamColumn
            key={team.id}
            team={team}
            players={players}
            onTeamChange={onTeamChange}
            onMove={handleMove}
            onRemove={onRemove}
          />
        ))}
      </div>
    </DndProvider>
  );
}

export default TeamAssignment; 